/**
 * Thin wrapper around the /predictions/* endpoints shipped in
 * `backend/app/routers/predictions.py`.
 *
 * Unlike `src/api/analytics.ts` these calls are ALWAYS live — predictions
 * represent current model state and should not be mocked. If the backend is
 * unreachable, the caller sees a network error and renders a red state.
 *
 * Response shapes mirror `backend/app/schemas/predictions.py` one-for-one.
 *
 * Role walls: the backend enforces them. The wrapper doesn't.
 *   - ANALYTICS_READ_ROLES (COORDINATOR+) can hit GET /predictions/r-lean
 *   - 403 is therefore not a frontend bug — surface it to the user as
 *     "Your role does not permit this action."
 */
import client from '@/api/client';

// ============================================================================
// Type definitions mirroring backend/app/schemas/predictions.py
// ============================================================================

export interface RLeanRow {
  voter_id: number;
  first_name: string;
  last_name: string;
  address: string;
  precinct_num: number;
  p_turnout: number;
  r_lean: number;
  composite_score: number;
}

export interface RLeanResponse {
  items: RLeanRow[];
  total: number;
}

// Shape returned by GET /predictions/r-lean (see backend schemas/prediction.py).
interface RLeanBackendResponse {
  election_date: string;
  election_type: string;
  total_rows: number;
  rows: RLeanRow[];
}

// ============================================================================
// Fetch functions
// ============================================================================

export interface FetchRLeanParams {
  electionDate?: string;
  electionType?: string;
  precinctNum?: number | null;
  minTurnout?: number;
  limit?: number;
}

/**
 * Fetch R-lean targeting data from GET /predictions/r-lean.
 *
 * Parameters:
 *   - electionDate: ISO date string (e.g. "2026-08-04")
 *   - electionType: string (e.g. "primary", "general")
 *   - precinctNum: optional precinct number to filter
 *   - minTurnout: optional minimum p_turnout threshold (0.0–1.0)
 *   - limit: optional row limit (default 25)
 *
 * Returns: RLeanResponse with items array (already sorted by composite_score DESC)
 *          and total count.
 *
 * Throws: axios error. Caller should use formatPredictionsError() to map
 *         status codes to user-facing strings.
 */
export async function fetchRLean(params: FetchRLeanParams): Promise<RLeanResponse> {
  const queryParams: Record<string, string | number> = {};

  if (params.electionDate) {
    queryParams.election_date = params.electionDate;
  }
  if (params.electionType) {
    queryParams.election_type = params.electionType;
  }
  if (params.precinctNum !== undefined && params.precinctNum !== null) {
    queryParams.precinct_num = params.precinctNum;
  }
  if (params.minTurnout !== undefined) {
    queryParams.min_turnout = params.minTurnout;
  }
  if (params.limit !== undefined) {
    queryParams.limit = params.limit;
  }

  const res = await client.get<RLeanBackendResponse>('/predictions/r-lean', {
    params: queryParams,
  });
  return {
    items: res.data.rows ?? [],
    total: res.data.total_rows ?? 0,
  };
}

/**
 * Error formatter for predictions API calls.
 *
 * Maps HTTP status codes to user-facing messages. Follows the same pattern
 * as formatCanvassError in src/api/canvass.ts.
 */
export function formatPredictionsError(err: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  const status = e?.response?.status as number | undefined;
  const detail = e?.response?.data?.detail as string | undefined;

  if (status === 401) {
    return 'Not authenticated. Log in and try again.';
  }
  if (status === 403) {
    return (
      'Access Denied — R-Lean Targeting requires coordinator+ role.' +
      (detail ? ` (${detail})` : '')
    );
  }
  if (status === 404) {
    return detail || 'Not found.';
  }
  if (status === 422) {
    return detail || 'Invalid filter parameters.';
  }
  if (!status) {
    return 'Could not reach the backend.';
  }
  return detail || `Backend error ${status}.`;
}

// ============================================================================
// Turnout accuracy (GET /predictions/turnout-accuracy)
// ============================================================================

export interface TurnoutAccuracyRow {
  jurisdiction_label: string;
  precinct_nums: number[];
  registered_voters_official: number | null;
  voters_on_roll: number;
  ballots_cast_election_day: number;
  ballots_cast_allocated: number;
  predicted_ballots: number;
  error: number;
  pct_error: number | null;
  actual_turnout_pct: number | null;
  predicted_turnout_pct: number | null;
}

export interface TurnoutAccuracyResponse {
  election_date: string;
  election_type: string;
  model_version: string | null;
  jurisdictions: TurnoutAccuracyRow[];
  excluded_jurisdictions: string[];
  absentee_ballots: number;
  absentee_allocation: string;
  predicted_total: number;
  actual_total: number;
  calibration_ratio: number | null;
  mean_abs_error: number | null;
  mean_abs_pct_error: number | null;
  correlation: number | null;
  worst_over_label: string | null;
  worst_under_label: string | null;
}

/**
 * Fetch the predicted-vs-certified turnout diff for one election.
 *
 * 404 means no certified results have been imported yet — the view renders
 * that as an explanatory empty state, not an error banner.
 */
export async function fetchTurnoutAccuracy(params: {
  electionDate: string;
  electionType: string;
}): Promise<TurnoutAccuracyResponse> {
  const res = await client.get<TurnoutAccuracyResponse>(
    '/predictions/turnout-accuracy',
    {
      params: {
        election_date: params.electionDate,
        election_type: params.electionType,
      },
    },
  );
  return res.data;
}
